// A canteen, footprint-native (2x2) as a plot: a mess hall across the back with
// a serving window and chimney, and outdoor seating (two trestle tables with
// benches) out front where the workers eat. Ported 1:1 from age-of-mcp's
// canteen.ts: a local hall() generator plus the shared foundationSlab/scaffold
// and diningSet/barrel props.
import * as THREE from "three";
import { defineModel, sitEat, leanLoad, foundationSlab, scaffold, diningSet, barrel, type Part } from "../../lib";

const WALL = "#caa46a";
const ROOF = "#8a5a34";
const STONE = "#6a6a6a";
const SMOKE = "#cfcfcf";
const DOOR = "#5a3c24";
const POT = "#3a3a3a";
const STEW = "#c66a2c";
const TRIM = "#e6dcc2";

const BASE = 0.14;
const HALLZ = -0.42;
const HALL_D = 1.0;
const FRONT = HALLZ + HALL_D / 2;

function hall(h: number): Part {
  return { geometry: new THREE.BoxGeometry(1.6, h, HALL_D), color: WALL, position: [0, BASE + h / 2, HALLZ] };
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.94)];
  if (s === 1) return [foundationSlab(1.94), hall(0.36), ...scaffold(1.6, 0.8)];
  if (s === 2) return [foundationSlab(1.94), hall(0.72), ...scaffold(1.6, 1.4)];

  const wallH = 0.86, top = BASE + wallH, rise = 0.5, halfD = HALL_D / 2 + 0.12;
  const slope = Math.hypot(halfD, rise), ang = Math.atan2(rise, halfD);
  const dz = FRONT + 0.02;
  return [
    foundationSlab(1.94),
    hall(wallH),
    { geometry: new THREE.BoxGeometry(1.74, 0.08, slope), color: ROOF, position: [0, top + rise / 2, HALLZ + halfD / 2], rotation: [ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(1.74, 0.08, slope), color: ROOF, position: [0, top + rise / 2, HALLZ - halfD / 2], rotation: [-ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(1.8, 0.08, 0.08), color: TRIM, position: [0, top + rise, HALLZ] },
    { geometry: new THREE.BoxGeometry(0.26, 0.7, 0.26), color: STONE, position: [-0.6, 1.0, HALLZ - 0.2] },
    { geometry: new THREE.BoxGeometry(0.24, 0.16, 0.24), color: SMOKE, position: [-0.6, 1.45, HALLZ - 0.2] },
    { geometry: new THREE.BoxGeometry(0.6, 0.34, 0.06), color: "#2a2018", position: [0.34, BASE + 0.5, dz] },
    { geometry: new THREE.BoxGeometry(0.7, 0.06, 0.07), color: TRIM, position: [0.34, BASE + 0.7, dz] },
    { geometry: new THREE.BoxGeometry(0.34, 0.66, 0.05), color: DOOR, position: [-0.42, BASE + 0.33, dz] },
    { geometry: new THREE.CylinderGeometry(0.16, 0.13, 0.2, 10), color: POT, position: [0.34, BASE + 0.1, dz + 0.18] },
    { geometry: new THREE.CylinderGeometry(0.15, 0.15, 0.04, 10), color: STEW, position: [0.34, BASE + 0.22, dz + 0.18] },
    { geometry: new THREE.BoxGeometry(0.12, 0.12, 0.12), color: SMOKE, position: [0.34, BASE + 0.44, dz + 0.18] },
    ...diningSet(-0.42, 0.55),
    ...diningSet(0.46, 0.55),
    barrel(-0.78, 0.2),
  ];
}

export const canteen = defineModel({
  id: "canteen",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Canteen",
  tags: ["building","canteen","low-poly","age-of-mcp","classic"],
  summary: "Low-poly canteen.",
  description: "A mess hall where workers eat, kept stocked with food.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "seat-0", position: [-0.54, 0.28, 0.29], facing: 0 },
    { id: "seat-1", position: [-0.3, 0.28, 0.29], facing: 0 },
    { id: "seat-2", position: [-0.54, 0.28, 0.81], facing: Math.PI },
    { id: "seat-3", position: [-0.3, 0.28, 0.81], facing: Math.PI },
    { id: "seat-4", position: [0.34, 0.28, 0.29], facing: 0 },
    { id: "seat-5", position: [0.58, 0.28, 0.29], facing: 0 },
    { id: "seat-6", position: [0.34, 0.28, 0.81], facing: Math.PI },
    { id: "seat-7", position: [0.58, 0.28, 0.81], facing: Math.PI },
    { id: "serve", position: [0.34, 0, 0.42], facing: Math.PI, description: "The serving window." },
  ],
  interactions: [
    { id: "dine", label: "Eating", station: "seat-0", variants: ["complete"], ...sitEat() },
    { id: "load", label: "Loading supplies", station: "serve", variants: ["complete"], ...leanLoad() },
  ],
});
